import { Navigate, useLocation, useParams } from "react-router-dom";
import { ReportsListingPage } from "reports/pages/ReportsListingPage";
import ReportListItem from "../../components/ReportsPage/ReportListItem";
import { startCase } from "lodash";
import {
  CustomReportWithSchedule,
  ReportScheduleType,
} from "components/ReportsPage/reportPageTypes";
import { useWorkScheduleConfigJsonListAtom } from "store/atoms";

export const ReportsListingRouter = () => {
  const { ckey } = useParams();
  const search = useLocation().search;
  const searchParams = new URLSearchParams(search);
  const { pathname } = useLocation();
  const [workScheduleConfigList] = useWorkScheduleConfigJsonListAtom();
  if (
    searchParams.get("workSchedule") === null &&
    workScheduleConfigList.length > 1
  ) {
    // if no work schedule param redirect to schedule page for section
    const redirectLink = pathname?.replace("reports", "schedule");
    // storybook-cypress url is /iframe.html

    return <Navigate to={`${redirectLink}`} />;
  }
  switch (ckey) {
    case "pci":
      return (
        <ReportsListingPage
          customReportItems={[pciCustomReportItem(searchParams)]}
        />
      );
    case "pciuk":
      return (
        <ReportsListingPage
          customReportItemsWithSchedule={[pciukCustomReportItemWithSchedule]}
        />
      );
    case "kruger":
      return (
        <ReportsListingPage
          customReportItemsWithSchedule={[krugerCustomReportItemWithSchedule]}
        />
      );
    case "nb":
      return (
        <ReportsListingPage
          customReportItemsWithSchedule={[nbCustomReportItemWithSchedule]}
        />
      );
    case "naturesway":
      return (
        <ReportsListingPage customReportItems={[natureswayCustomReportItem]} />
      );
    case "natureswaytwo":
      return (
        <ReportsListingPage
          customReportItems={[natureswaytwoCustomReportItem]}
        />
      );
    case "nwmason":
      return (
        <ReportsListingPage
          customReportItems={[natureswaymasonCustomReportItem]}
        />
      );
    case "nwmason2":
      return (
        <ReportsListingPage
          customReportItems={[natureswaymasontwoCustomReportItem]}
        />
      );
    case "portlandforge":
      return (
        <ReportsListingPage
          customReportItemsWithSchedule={[
            portlandForgeCustomReportItemWithSchedule,
            portlandForgeCustomReportItemWithScheduleForWeeklySku,
          ]}
        />
      );
    case "portlandforge-lebanon":
      return (
        <ReportsListingPage
          customReportItemsWithSchedule={[
            portlandForgeLebanonCustomReportItemWithSchedule,
            portlandForgeLebanonCustomReportItemWithScheduleForWeeklySku,
          ]}
        />
      );
    case "quepasa":
      return (
        <ReportsListingPage
          customReportItemsWithSchedule={[
            quepasaCustomReportItemWithSchedule,
            quepasaCustomReportItemWithScheduleForWeeklySku,
          ]}
        />
      );
    case "eccomfg-langley":
      return (
        <ReportsListingPage
          customReportItemsWithSchedule={[
            eccomfglangleyCustomReportItemWithScheduleForWeeklySku,
          ]}
        />
      );
    case "eccomfg-calgary":
      return (
        <ReportsListingPage
          customReportItemsWithSchedule={[
            eccomfgcalgaryCustomReportItemWithScheduleForWeeklySku,
          ]}
        />
      );

    default:
      return <ReportsListingPage />;
  }
};

const pciukCustomReportItemWithSchedule: CustomReportWithSchedule = {
  reportTitle: "Weekly SKU Performance",
  reportPath: "sku-metrics",
  reportGroups: ["All Rooms"],
  reportScheduleType: ReportScheduleType.WEEKLY,
};
const krugerCustomReportItemWithSchedule: CustomReportWithSchedule = {
  reportTitle: "Weekly Tagging Performance",
  reportPath: "tagging",
  reportScheduleType: ReportScheduleType.WEEKLY,
};
const nbCustomReportItemWithSchedule: CustomReportWithSchedule = {
  reportTitle: "Operator TPM",
  reportPath: "OperatorTPM",
  reportScheduleType: ReportScheduleType.WEEKLY,
  reportGroups: ["Day", "Evening"],
  multiDayReportConfig: [
    { startDayOfWeek: 3, startHour: 0, startMinute: 0 },
    { startDayOfWeek: 5, startHour: 0, startMinute: 0 },
  ],
};

const pciCustomReportItem = (
  searchParams: URLSearchParams = new URLSearchParams()
) => {
  const defaultMachines = [
    "A2",
    "A8",
    "A9",
    "B1C2",
    "B5C4",
    "B7",
    "B8",
    "C8",
    "D3",
    "D4",
    "D5",
    "D6",
    "D7",
    "D8",
    "E3",
    "E2",
    "E4",
    "E5",
    "F1",
    "F4",
    "G2A",
    "G3",
    "G4",
    "G5",
    "G6",
    "G9",
    "H1",
    "H2",
    "H3",
    "H4",
    "H5",
    "H6",
  ];
  const H7Machines = [
    "H7",
    "Cell 01",
    "Cell 02",
    "Cell 03",
    "Cell 04",
    "Cell 05",
    "Tray Handler 01",
    "Tray Handler 02",
    "Tray Handler 03",
    "Tray Handler 04",
    "Tray Handler 06",
    "Tray Handler 07",
    "A5",
    "G2B",
  ];
  const workSchedule = searchParams?.get("workSchedule");
  const machines = workSchedule === "2" ? H7Machines : defaultMachines;
  return {
    reportTitle: "Focus Room",
    reportContent: machines.map(machine => (
      <ReportListItem
        key={machine}
        reportTitle={machine}
        portalReportPath={`focusroom/${machine}?workSchedule=${workSchedule}`}
      />
    )),
  };
};

const WEEK_TO_DATE_EXPANDABLE_TITLE = "Week To Date";
const natureswayCustomReportItem = {
  reportTitle: WEEK_TO_DATE_EXPANDABLE_TITLE,
  reportContent: ["main_line", "merril", "cremer", "liquids"].map(machine => (
    <ReportListItem
      key={machine}
      reportTitle={startCase(machine)}
      portalReportPath={`week-to-date?machineId=${machine}`}
    />
  )),
};

const natureswaytwoCustomReportItem = {
  reportTitle: WEEK_TO_DATE_EXPANDABLE_TITLE,
  reportContent: ["encapsulation_1"].map(machine => (
    <ReportListItem
      key={machine}
      reportTitle={startCase(machine)}
      portalReportPath={`week-to-date?machineId=${machine}`}
    />
  )),
};

const natureswaymasontwoCustomReportItem = {
  reportTitle: WEEK_TO_DATE_EXPANDABLE_TITLE,
  reportContent: ["manufacturing"].map(machine => (
    <ReportListItem
      key={machine}
      reportTitle={startCase(machine)}
      portalReportPath={`week-to-date?machineId=${machine}`}
    />
  )),
};

const natureswaymasonCustomReportItem = {
  reportTitle: WEEK_TO_DATE_EXPANDABLE_TITLE,
  reportContent: ["packaging"].map(machine => (
    <ReportListItem
      key={machine}
      reportTitle={startCase(machine)}
      portalReportPath={`week-to-date?machineId=${machine}`}
    />
  )),
};

const portlandForgeCustomReportItemWithSchedule: CustomReportWithSchedule = {
  reportTitle: "Shiftly SKU Performance",
  reportPath: "sku-metrics",
  reportGroups: ["Forging"],
  reportScheduleType: ReportScheduleType.SHIFTLY,
};

const portlandForgeCustomReportItemWithScheduleForWeeklySku: CustomReportWithSchedule =
  {
    reportTitle: "Weekly SKU Performance",
    reportPath: "sku-metrics",
    reportGroups: ["Forging"],
    reportScheduleType: ReportScheduleType.WEEKLY,
  };

const portlandForgeLebanonCustomReportItemWithSchedule: CustomReportWithSchedule =
  {
    reportTitle: "Shiftly SKU Performance",
    reportPath: "sku-metrics",
    reportGroups: ["Presses"],
    reportScheduleType: ReportScheduleType.SHIFTLY,
  };

const portlandForgeLebanonCustomReportItemWithScheduleForWeeklySku: CustomReportWithSchedule =
  {
    reportTitle: "Weekly SKU Performance",
    reportPath: "sku-metrics",
    reportGroups: ["Presses"],
    reportScheduleType: ReportScheduleType.WEEKLY,
  };

const quepasaCustomReportItemWithSchedule: CustomReportWithSchedule = {
  reportTitle: "Shiftly SKU Performance",
  reportPath: "sku-metrics",
  reportGroups: ["All"],
  reportScheduleType: ReportScheduleType.SHIFTLY,
};

const quepasaCustomReportItemWithScheduleForWeeklySku: CustomReportWithSchedule =
  {
    reportTitle: "Weekly SKU Performance",
    reportPath: "sku-metrics",
    reportGroups: ["All"],
    reportScheduleType: ReportScheduleType.WEEKLY,
  };

const eccomfglangleyCustomReportItemWithScheduleForWeeklySku: CustomReportWithSchedule =
  {
    reportTitle: "Weekly SKU Performance",
    reportPath: "sku-metrics",
    reportGroups: ["Pipeline", "CNC Brake", "CNC Press", "Ecco Duct"],
    reportScheduleType: ReportScheduleType.WEEKLY,
  };

const eccomfgcalgaryCustomReportItemWithScheduleForWeeklySku: CustomReportWithSchedule =
  {
    reportTitle: "Weekly SKU Performance",
    reportPath: "sku-metrics",
    reportGroups: [
      "GM-Pieces",
      "GM-Feet",
      "ER-Pieces",
      "CNC-Sheets",
      "CNC-Bends",
      "Elbows",
      "Fittings",
    ],
    reportScheduleType: ReportScheduleType.WEEKLY,
  };
